import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { PartOrderLine, PartOrderLineStatus } from '@techgitter/contracts';
import { fetchPartOrderLines } from '../../services/partOrdersApi';
import { useSignalREvent } from '../../hooks/useSignalR';

interface Props {
  workitemId: string;
}

const STATUS_LABEL: Record<PartOrderLineStatus, string> = {
  to_order: 'To Order',
  ordered: 'Ordered',
  partially_received: 'Partial',
  received: 'Received',
  cancelled: 'Cancelled',
};

const STATUS_COLOR: Record<PartOrderLineStatus, 'default' | 'warning' | 'info' | 'success'> = {
  to_order: 'warning',
  ordered: 'info',
  partially_received: 'info',
  received: 'success',
  cancelled: 'default',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

/**
 * Read-only part-orders panel for the workitem detail page.
 *
 * Lists every PartOrderLine whose workitemId matches. Subscribes to
 * `parts:line_received` and `parts:order_placed` to live-refresh — the panel
 * is purely informational; place-order / receive actions live on the Part
 * Orders page.
 */
export const PartsPanel: React.FC<Props> = ({ workitemId }) => {
  const [lines, setLines] = useState<PartOrderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setLoading(true);
        setError(null);
        const items = await fetchPartOrderLines({ workitemId }, signal);
        setLines(items);
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load parts');
      } finally {
        setLoading(false);
      }
    },
    [workitemId],
  );

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  useSignalREvent('parts:line_received', () => refresh());
  useSignalREvent('parts:order_placed', () => refresh());
  useSignalREvent('parts:to_order', () => refresh());

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (lines.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No parts ordered for this job.
      </Typography>
    );
  }

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>SKU</TableCell>
            <TableCell>Vendor</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>ETA</TableCell>
            <TableCell>Received</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.id}>
              <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{line.description}</TableCell>
              <TableCell>{line.sku || '—'}</TableCell>
              <TableCell>{line.vendorName || '—'}</TableCell>
              <TableCell align="right">
                {line.qty}
                {line.receivedQty > 0 && line.receivedQty < line.qty && ` (${line.receivedQty} in)`}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={STATUS_LABEL[line.status] ?? line.status}
                  color={STATUS_COLOR[line.status] ?? 'default'}
                />
              </TableCell>
              <TableCell>{formatDate(line.expectedDeliveryDate)}</TableCell>
              <TableCell>{formatDate(line.receivedDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
